import type { TrackStatus, TrackPatchInput } from '../shared/contracts';
import { authClient } from './auth';
import type { CompletedPart, UploadSession, UploadStatus } from './uploader';

/** Carries Better Auth's error code so the UI can explain what actually happened. */
export class AuthError extends Error {
  constructor(
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export type SessionUserDto = {
  id: string;
  username: string;
  displayUsername: string | null;
  email: string;
  role: 'member' | 'owner';
};

export type AdminUserDto = {
  id: string;
  username: string;
  displayUsername: string | null;
  email: string;
  createdAt: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  role: 'member' | 'owner';
  trackCount: number;
};

/**
 * Fields marked optional are owner-only: the public payload is built from an allowlist
 * on the server, so a visitor's listing simply does not carry them.
 */
export type TrackDto = {
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  genre: string | null;
  description: string | null;
  recordedYear: number | null;
  durationSeconds: number | null;
  sizeBytes: number;
  status: TrackStatus;
  coverUrl: string | null;
  uploadedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether the server has finished baking this track's waveform. */
  hasWaveform: boolean;
  /** Who uploaded it. Null only for a track whose owner row has gone missing. */
  uploader: { username: string; displayUsername: string | null } | null;
  /**
   * Whether *this* viewer may edit or delete it, decided by the server. The client
   * never re-derives this from an id, so the UI cannot disagree with the API.
   */
  canManage: boolean;
  processingError?: string | null;
  audioObjectKey?: string;
  coverObjectKey?: string | null;
  originalFilename?: string;
  trashedAt?: string | null;
  purgeAfter?: string | null;
};
export interface ApiClient {
  /** The signed-in user, or null. `pendingUserCount` is owner-only. */
  session(): Promise<{ user: SessionUserDto | null; pendingUserCount?: number }>;
  signIn(username: string, password: string): Promise<void>;
  signUp(input: { username: string; email: string; password: string }): Promise<void>;
  signOut(): Promise<void>;
  adminUsers(): Promise<AdminUserDto[]>;
  approveUser(id: string): Promise<void>;
  rejectUser(id: string): Promise<void>;
  listTracks(options?: {
    view?: 'library' | 'trash';
    sort?: 'newest' | 'title' | 'year' | 'duration';
    query?: string;
  }): Promise<{
    items: TrackDto[];
    storage?: { objectCount: number; totalBytes: number; quotaBytes?: number };
  }>;
  updateTrack(id: string, patch: TrackPatchInput): Promise<TrackDto>;
  trashTrack(id: string): Promise<TrackDto>;
  restoreTrack(id: string): Promise<TrackDto>;
  playbackUrl(id: string): Promise<{ url: string; expiresAt: string }>;
  createUpload(input: {
    filename: string;
    contentType: string;
    sizeBytes: number;
    lastModified: number;
  }): Promise<UploadSession>;
  uploadStatus(id: string): Promise<UploadStatus>;
  signUploadParts(id: string, partNumbers: number[]): Promise<{
    expiresAt: string;
    parts: { partNumber: number; url: string }[];
  }>;
  completeUpload(id: string, parts: CompletedPart[]): Promise<TrackDto>;
  cancelUpload(id: string): Promise<void>;
  createCoverUpload(id: string, input: { contentType: string; sizeBytes: number }): Promise<{
    id: string;
    url: string;
    expiresAt: string;
  }>;
  completeCoverUpload(id: string, uploadId: string): Promise<TrackDto>;
  createBackup(): Promise<{ key: string; sizeBytes: number; createdAt: string }>;
}

type ApiEnvelope<T> = { data: T };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => null)) as
    | ApiEnvelope<T>
    | { error?: { message?: string } }
    | null;
  if (!response.ok) {
    const message = payload && 'error' in payload ? payload.error?.message : undefined;
    throw new Error(message || `Request failed with status ${response.status}`);
  }
  if (!payload || !('data' in payload)) throw new Error('Server returned an invalid response');
  return payload.data;
}

async function decideUser(id: string, decision: 'approve' | 'reject'): Promise<void> {
  await request(`/api/admin/users/${encodeURIComponent(id)}/${decision}`, { method: 'POST' });
}

export const browserApi: ApiClient = {
  async session() {
    // 401 is the anonymous case, not a failure: /api/me is behind the default-deny hook.
    const response = await fetch('/api/me', { credentials: 'same-origin' });
    if (response.status === 401) return { user: null };
    if (!response.ok) throw new Error('Could not read the session');
    return (await response.json()).data;
  },
  async signIn(username, password) {
    const { error } = await authClient.signIn.username({ username, password });
    if (error) throw new AuthError(error.code, error.message ?? 'Could not sign in');
  },
  async signUp(input) {
    const { error } = await authClient.signUp.email({
      email: input.email,
      name: input.username,
      password: input.password,
      username: input.username,
    });
    if (error) throw new AuthError(error.code, error.message ?? 'Could not create the account');
  },
  async signOut() {
    await authClient.signOut();
  },
  adminUsers: async () => (await request<{ users: AdminUserDto[] }>('/api/admin/users?status=all')).users,
  approveUser: (id) => decideUser(id, 'approve'),
  rejectUser: (id) => decideUser(id, 'reject'),
  listTracks(options = {}) {
    const query = new URLSearchParams({
      view: options.view ?? 'library',
      sort: options.sort ?? 'newest',
    });
    if (options.query) query.set('q', options.query);
    return request(`/api/tracks?${query}`);
  },
  updateTrack: (id, patch) =>
    request(`/api/tracks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  trashTrack: (id) =>
    request(`/api/tracks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreTrack: (id) =>
    request(`/api/tracks/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  playbackUrl: (id) =>
    request(`/api/tracks/${encodeURIComponent(id)}/playback-url`, { method: 'POST' }),
  createUpload: (input) =>
    request('/api/uploads', { method: 'POST', body: JSON.stringify(input) }),
  uploadStatus: (id) => request(`/api/uploads/${encodeURIComponent(id)}`),
  signUploadParts: (id, partNumbers) =>
    request(`/api/uploads/${encodeURIComponent(id)}/parts`, {
      method: 'POST',
      body: JSON.stringify({ partNumbers }),
    }),
  completeUpload: (id, parts) =>
    request(`/api/uploads/${encodeURIComponent(id)}/complete`, {
      method: 'POST',
      body: JSON.stringify({ parts }),
    }),
  async cancelUpload(id) {
    await request(`/api/uploads/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  createCoverUpload: (id, input) =>
    request(`/api/tracks/${encodeURIComponent(id)}/cover-upload`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  completeCoverUpload: (id, uploadId) =>
    request(`/api/tracks/${encodeURIComponent(id)}/cover-upload/complete`, {
      method: 'POST',
      body: JSON.stringify({ uploadId }),
    }),
  createBackup: () => request('/api/system/backup', { method: 'POST' }),
};
